import { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { compensationAPI } from '../../lib/api';
import { fmt } from './_shared';
import { formatPhoneInput } from '@/lib/utils';

const REVIEW_ACTIONS = [
  { value: 'match_existing_agent', label: 'Match Existing Agent (provide NPN)' },
  { value: 'create_agent', label: 'Create Agent (Quick Create — Pending Setup)' },
  { value: 'manual_correction', label: 'Manual Correction (patch the record)' },
  { value: 'mark_disputed', label: 'Mark Disputed' },
  { value: 'ignore_exclude', label: 'Ignore / Exclude this record' },
  { value: 'merge_duplicate', label: 'Merge with duplicate' },
  { value: 'keep_both', label: 'Keep both (not a duplicate)' },
  { value: 'mark_not_duplicate', label: 'Mark Not a Duplicate' },
  { value: 'approved_pay_expected', label: 'Pay Expected amount (legacy)' },
  { value: 'approved_pay_actual', label: 'Pay Actual amount (legacy)' },
  { value: 'manual_adjustment', label: 'Manual adjustment (admin will set, legacy)' },
  { value: 'resolved', label: 'Resolved — Approve (legacy)' },
];

export function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [dialog, setDialog] = useState({ open: false, review: null });
  const [form, setForm] = useState({
    action: 'manual_correction', notes: '',
    matched_agent_npn: '',
    new_agent: { npn: '', agency_id: '', first_name: '', last_name: '', email: '', phone: '', state: '' },
    correction: { writing_agent_npn: '', actual_carrier_paid: '', expected_amount: '', carrier_id: '', product_category: '' },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await compensationAPI.listReviews({ status: 'open' });
      setReviews(r.data.reviews || []);
    } catch { toast.error('Failed to load reviews'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!dialog.open) return;
    import('../../lib/api').then(({ agencyAPI }) => {
      if (!agencyAPI) return;
      agencyAPI.list().then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data.agencies || []);
        setAgencies(list);
      }).catch(() => {});
    });
  }, [dialog.open]);

  const openResolve = (rv) => {
    setDialog({ open: true, review: rv });
    const initialAction = rv.review_type === 'duplicate' ? 'mark_not_duplicate'
      : (rv.reason || '').toLowerCase().includes('not matched') ? 'match_existing_agent'
      : 'manual_correction';
    setForm({
      action: initialAction, notes: '',
      matched_agent_npn: rv.writing_agent_npn || '',
      new_agent: { npn: rv.writing_agent_npn || '', agency_id: '', first_name: '', last_name: '', email: '', phone: '', state: '' },
      correction: { writing_agent_npn: '', actual_carrier_paid: '', expected_amount: '', carrier_id: '', product_category: '' },
    });
  };

  const submit = async () => {
    if (!form.notes.trim()) { toast.error('Notes are required'); return; }
    const payload = { action: form.action, notes: form.notes };
    if (form.action === 'match_existing_agent') {
      if (!form.matched_agent_npn.trim()) { toast.error('Agent NPN is required'); return; }
      payload.matched_agent_npn = form.matched_agent_npn.trim();
    } else if (form.action === 'create_agent') {
      const a = form.new_agent;
      if (!a.npn || !a.agency_id || !a.first_name || !a.last_name) {
        toast.error('NPN, Agency, First Name and Last Name are required'); return;
      }
      payload.created_agent_payload = a;
    } else if (form.action === 'manual_correction') {
      const c = form.correction;
      const cleaned = Object.fromEntries(Object.entries(c).filter(([, v]) => v !== '' && v !== null));
      if (!Object.keys(cleaned).length) { toast.error('Provide at least one correction'); return; }
      if (cleaned.actual_carrier_paid) cleaned.actual_carrier_paid = Number(cleaned.actual_carrier_paid);
      if (cleaned.expected_amount) cleaned.expected_amount = Number(cleaned.expected_amount);
      payload.corrected_record_patch = cleaned;
    }
    try {
      await compensationAPI.resolveReview(dialog.review.review_id, payload);
      toast.success('Review resolved');
      setDialog({ open: false, review: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resolve');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="comp-reviews-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
        <span className="text-sm text-zinc-500 ml-auto">{reviews.length} open</span>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Policy Holder</TableHead>
              <TableHead>Carrier</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Expected</TableHead>
              <TableHead className="text-right">Actual</TableHead>
              <TableHead className="text-right">Diff</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reviews.length === 0 && !loading && (
              <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-6">No reviews pending.</TableCell></TableRow>
            )}
            {reviews.map((rv) => (
              <TableRow key={rv.review_id} data-testid={`comp-review-row-${rv.review_id}`}>
                <TableCell className="font-medium">{rv.policy_holder}</TableCell>
                <TableCell className="text-sm">{rv.carrier_id}</TableCell>
                <TableCell>
                  {rv.review_type === 'duplicate' ? (
                    <Badge className="bg-orange-100 text-orange-800">Duplicate {rv.duplicate_confidence ? `${rv.duplicate_confidence}%` : ''}</Badge>
                  ) : (
                    <Badge variant="outline">Review</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">{fmt(rv.expected_amount)}</TableCell>
                <TableCell className="text-right">{fmt(rv.actual_carrier_paid)}</TableCell>
                <TableCell className="text-right">{fmt(rv.difference)}</TableCell>
                <TableCell className="text-xs text-zinc-600 max-w-md truncate" title={rv.reason}>{rv.reason}</TableCell>
                <TableCell>
                  <Button size="sm" onClick={() => openResolve(rv)} data-testid={`comp-review-resolve-${rv.review_id}`}>Resolve</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={dialog.open} onOpenChange={(v) => !v && setDialog({ open: false, review: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Resolve Review</DialogTitle>
            <DialogDescription>{dialog.review?.policy_holder} — {dialog.review?.reason}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Action</Label>
              <Select value={form.action} onValueChange={(v) => setForm((p) => ({ ...p, action: v }))}>
                <SelectTrigger data-testid="comp-review-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REVIEW_ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.action === 'match_existing_agent' && (
              <div>
                <Label>Existing Agent NPN *</Label>
                <Input value={form.matched_agent_npn} data-testid="comp-review-match-npn"
                       onChange={(e) => setForm((p) => ({ ...p, matched_agent_npn: e.target.value }))} />
              </div>
            )}
            {form.action === 'create_agent' && (
              <div className="rounded border p-3 space-y-3 bg-zinc-50">
                <div className="text-sm font-medium text-zinc-700">Quick Create Agent (Pending Setup)</div>
                <div className="grid gap-2 md:grid-cols-2">
                  <div><Label>NPN *</Label><Input data-testid="qc-agent-npn" value={form.new_agent.npn} onChange={(e) => setForm((p) => ({ ...p, new_agent: { ...p.new_agent, npn: e.target.value } }))} /></div>
                  <div>
                    <Label>Agency *</Label>
                    <Select value={form.new_agent.agency_id} onValueChange={(v) => setForm((p) => ({ ...p, new_agent: { ...p.new_agent, agency_id: v } }))}>
                      <SelectTrigger data-testid="qc-agent-agency"><SelectValue placeholder="Select agency…" /></SelectTrigger>
                      <SelectContent>
                        {agencies.map((a) => <SelectItem key={a.agency_id} value={a.agency_id}>{a.agency_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>First Name *</Label><Input data-testid="qc-agent-first" value={form.new_agent.first_name} onChange={(e) => setForm((p) => ({ ...p, new_agent: { ...p.new_agent, first_name: e.target.value } }))} /></div>
                  <div><Label>Last Name *</Label><Input data-testid="qc-agent-last" value={form.new_agent.last_name} onChange={(e) => setForm((p) => ({ ...p, new_agent: { ...p.new_agent, last_name: e.target.value } }))} /></div>
                  <div><Label>Email</Label><Input value={form.new_agent.email} onChange={(e) => setForm((p) => ({ ...p, new_agent: { ...p.new_agent, email: e.target.value } }))} /></div>
                  <div><Label>Phone</Label><Input value={formatPhoneInput(form.new_agent.phone)} onChange={(e) => setForm((p) => ({ ...p, new_agent: { ...p.new_agent, phone: e.target.value } }))} /></div>
                  <div><Label>Resident State</Label><Input maxLength={2} value={form.new_agent.state} onChange={(e) => setForm((p) => ({ ...p, new_agent: { ...p.new_agent, state: e.target.value.toUpperCase() } }))} /></div>
                </div>
                <p className="text-xs text-zinc-500">Agent will be created with status <b>Pending Setup</b> + flags Incomplete Profile / Requires Invite.</p>
              </div>
            )}
            {form.action === 'manual_correction' && (
              <div className="rounded border p-3 space-y-2 bg-zinc-50">
                <div className="text-sm font-medium text-zinc-700">Patch record fields (leave blank to keep unchanged)</div>
                <div className="grid gap-2 md:grid-cols-2">
                  <div><Label>Writing Agent NPN</Label><Input value={form.correction.writing_agent_npn} onChange={(e) => setForm((p) => ({ ...p, correction: { ...p.correction, writing_agent_npn: e.target.value } }))} /></div>
                  <div><Label>Carrier ID</Label><Input value={form.correction.carrier_id} onChange={(e) => setForm((p) => ({ ...p, correction: { ...p.correction, carrier_id: e.target.value } }))} /></div>
                  <div><Label>Product Category</Label><Input value={form.correction.product_category} onChange={(e) => setForm((p) => ({ ...p, correction: { ...p.correction, product_category: e.target.value } }))} /></div>
                  <div><Label>Expected Amount</Label><Input type="number" value={form.correction.expected_amount} onChange={(e) => setForm((p) => ({ ...p, correction: { ...p.correction, expected_amount: e.target.value } }))} /></div>
                  <div><Label>Actual Carrier Paid</Label><Input type="number" value={form.correction.actual_carrier_paid} onChange={(e) => setForm((p) => ({ ...p, correction: { ...p.correction, actual_carrier_paid: e.target.value } }))} /></div>
                </div>
              </div>
            )}
            <div>
              <Label>Resolution Notes (required)</Label>
              <Textarea data-testid="comp-review-notes" value={form.notes}
                        onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                        rows={3} placeholder="Why was this decision made?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, review: null })}>Cancel</Button>
            <Button onClick={submit} data-testid="comp-review-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
